import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Banner, Button, Flex, LabeledData, LoadingIndicator } from "@jsq/ds/core";
import { FormSelect, useFormContext } from "@jsq/ds/forms";
import {
  AgentJobStatus,
  type ComplianceCaseTypeEnum,
  ComplianceObjectType,
} from "~/graphql/supergraphEnums";
import {
  type GetComplianceCaseQuery,
  AgentJobStatus as AgentJobStatusType,
} from "~/graphql/supergraphTypes";
import { AddressViewOnly } from "~/pages/Compliance/CaseDetailsDrawer/common/AddressViewOnly";
import {
  compareProfileWithExtractedData,
  ExtractedDataField,
  type ProfileDataField,
} from "~/pages/Compliance/CaseDetailsDrawer/Documents/DocumentManagement/DocumentDetailsRightSidebar/ProfileDetailsFormValues/ProfileConflictResolver/profileDataComparison";
import { getProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { AIFieldLabel } from "~/pages/Compliance/Common/AIFieldLabel";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import {
  useGuidedProfileCreation,
  useIsFatcaCrsEnabledForCase,
  usePicrRevampFeatureFlag,
} from "~/pages/Compliance/flags";
import { isJointProfile } from "~/pages/Compliance/utils/accountTypeUtils";
import { formatShortFormDate } from "~/utils/datetime";
import { extractChildProfiles } from "../../../utils";
import { useChangeAndResetDocumentData } from "../hooks/useChangeAndResetDocumentData";
import { ResetDocumentDataWarningModal } from "../ResetDocumentDataWarningModal";
import { useResetWarningModal } from "../ResetDocumentDataWarningModal/useResetWarningModal";
import { FormValues } from "../types";
import { isTaxDocumentType } from "../utils";
import { DocumentExtractionInfoCard } from "./DocumentExtractionInfoCard";
import {
  createManualProfileButton,
  createNewProfile,
  editProfileButton,
} from "./styles_vanilla.css";
import { getMatchingProfile } from "./utils";

export type Props = {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
  readonly: boolean;
  isExtracting?: boolean;
  extractedData?: ExtractedDataField | null;
  extractionStatus?: AgentJobStatusType;
  conflictsResolved?: boolean;
  documentName?: string;
  documentId: number;
};

export const ProfileDetailsFormValues = ({
  complianceCase,
  readonly,
  isExtracting,
  extractedData,
  extractionStatus,
  conflictsResolved,
  documentName = "",
  documentId,
}: Props) => {
  const { values, setFieldValue } = useFormContext<FormValues>();
  const isPicrRevampEnabled = usePicrRevampFeatureFlag();
  const guidedProfileCreation = useGuidedProfileCreation();
  const {
    caseId,
    caseType,
    isIndividualRootProfile,
    aiExtractionSupported: aiExtractionEnabled,
    fatcaCrsEnabled,
  } = useCurrentCaseContext();
  const hideChildProfileCreation = guidedProfileCreation && isIndividualRootProfile;
  const isFatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    caseType as ComplianceCaseTypeEnum | undefined,
    fatcaCrsEnabled,
  );

  const { changeAndReset } = useChangeAndResetDocumentData({
    caseId,
    documentId,
  });
  const [isExtractionFailedBannerDismissed, setIsExtractionFailedBannerDismissed] =
    useState(false);
  const history = useHistory();
  const location = useLocation();
  const allProfiles = extractChildProfiles(complianceCase, isPicrRevampEnabled);
  const currentlySelectedProfile = allProfiles.find(
    (profile) => String(profile?.id) === String(values.relatedProfile),
  );

  const matchingProfile = useMemo(
    () => getMatchingProfile(allProfiles, extractedData),
    [allProfiles, extractedData],
  );

  // Build profile data from the matching profile to compare
  const profileData = useMemo<ProfileDataField>(() => {
    const primaryAddress = matchingProfile?.addresses?.find((addr) => addr.isPrimaryDomicile);
    return {
      dateOfBirth: matchingProfile?.dateOfBirth,
      dateOfOrganization: matchingProfile?.dateOfOrganization,
      street: primaryAddress?.street,
      street2: primaryAddress?.street2,
      street3: primaryAddress?.street3,
      city: primaryAddress?.city,
      state: primaryAddress?.state?.name || primaryAddress?.province,
      postalCode: primaryAddress?.postalCode,
      country: primaryAddress?.country?.name,
    };
  }, [matchingProfile]);

  const isIndividual =
    currentlySelectedProfile?.complianceObjectType === ComplianceObjectType.Individual;

  const allowDedicatedJointFlow =
    guidedProfileCreation &&
    isJointProfile(
      currentlySelectedProfile?.accountType,
      currentlySelectedProfile?.complianceObjectType,
    );

  const differenceCount = useMemo(
    () =>
      aiExtractionEnabled && extractedData
        ? compareProfileWithExtractedData(profileData, extractedData, isIndividual)
        : 0,
    [aiExtractionEnabled, extractedData, profileData, isIndividual],
  );

  useEffect(() => {
    if (values.relatedProfile === "create-new") {
      void setFieldValue("relatedProfile", "");
    }
  }, [values.relatedProfile, setFieldValue]);

  useEffect(() => {
    if (
      extractionStatus === AgentJobStatus.Succeeded &&
      matchingProfile &&
      !values.relatedProfile
    ) {
      void setFieldValue("relatedProfile", String(matchingProfile.id));
    }
  }, [extractionStatus, matchingProfile, values.relatedProfile, setFieldValue]);

  const handleResolveConflictsClick = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.append("profileId", String(matchingProfile?.id));
    history.push({
      pathname: `${location.pathname}/upsert-profile/conflict-resolver`,
      search: params.toString(),
    });
  }, [location.search, location.pathname, matchingProfile?.id, history]);

  const handleCreateProfileClick = useCallback(() => {
    void setFieldValue("relatedProfile", "");
    history.push({
      pathname: `${location.pathname}/upsert-profile`,
      search: location.search,
    });
  }, [history, location.pathname, location.search, setFieldValue]);

  const handleEditProfile = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.append("profileId", String(currentlySelectedProfile?.id));
    const role = isIndividual ? "individual" : allowDedicatedJointFlow ? "joint" : "entity";
    history.push({
      pathname: `${location.pathname}/upsert-profile/${role}/edit-profile`,
      search: params.toString(),
    });
  }, [
    location.search,
    location.pathname,
    currentlySelectedProfile?.id,
    history,
    isIndividual,
    allowDedicatedJointFlow,
  ]);

  const cardConfig = useMemo(() => {
    if (extractionStatus === AgentJobStatus.Succeeded) {
      if (matchingProfile) {
        return !conflictsResolved && differenceCount > 0
          ? {
              key: "profile_found_with_conflicts" as const,
              onClick: handleResolveConflictsClick,
            }
          : { key: "profile_found" as const, onClick: null };
      } else if (extractedData && !values.relatedProfile) {
        return { key: "create_profile" as const, onClick: handleCreateProfileClick };
      }
    }
    return undefined;
  }, [
    extractionStatus,
    matchingProfile,
    extractedData,
    differenceCount,
    conflictsResolved,
    values.relatedProfile,
    handleResolveConflictsClick,
    handleCreateProfileClick,
  ]);

  const profileSelectedByAi = values.relatedProfile === String(matchingProfile?.id);

  const getProfileReadableName = useCallback(
    (profileId: string) => {
      const profile = allProfiles.find((p) => String(p?.id) === profileId);
      return profile ? getProfileName(profile) : profileId;
    },
    [allProfiles],
  );

  const isCurrentProfileRootProfile = useMemo(() => {
    const rootProfileId = complianceCase?.investingProfile?.id;
    return Boolean(rootProfileId && String(rootProfileId) === values.relatedProfile);
  }, [complianceCase?.investingProfile?.id, values.relatedProfile]);

  const shouldShowModal = useCallback(() => {
    return isTaxDocumentType(values.documentType) && isCurrentProfileRootProfile;
  }, [values.documentType, isCurrentProfileRootProfile]);

  const handleProfileChangeConfirm = useCallback(
    async (newValue: string) => {
      return changeAndReset({
        resetInfo: {
          profileId: Number(newValue),
          documentPurpose: null,
          documentStatus: null,
          subType: null,
        },
      });
    },
    [changeAndReset],
  );

  const handleProfileDirectChange = useCallback(
    (newValue: string) => {
      void setFieldValue("relatedProfile", newValue);
    },
    [setFieldValue],
  );

  const handleProfileChangeCancel = useCallback(
    (previousValue: string) => {
      void setFieldValue("relatedProfile", previousValue);
    },
    [setFieldValue],
  );

  const {
    showModal: showResetModal,
    pendingChange,
    isLoading: isModalLoading,
    handleFieldChange: handleProfileFieldChange,
    handleConfirm: handleResetConfirm,
    handleCancel: handleResetCancel,
  } = useResetWarningModal({
    onConfirm: handleProfileChangeConfirm,
    onCancel: handleProfileChangeCancel,
    onDirectChange: handleProfileDirectChange,
    getDocumentName: () => documentName,
    getCurrentValue: () => values.relatedProfile,
    getReadableValue: getProfileReadableName,
    changeType: "profile",
    shouldShowModal,
  });

  const handleProfileSelectChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const newValue = String(value);
      if (newValue === "create-new") {
        handleCreateProfileClick();
        return;
      }
      if (isFatcaCrsEnabled && values.relatedProfile) {
        handleProfileFieldChange(newValue);
      } else {
        void setFieldValue("relatedProfile", newValue);
      }
    },
    [
      isFatcaCrsEnabled,
      values.relatedProfile,
      handleProfileFieldChange,
      setFieldValue,
      handleCreateProfileClick,
    ],
  );

  if (isExtracting) {
    return <LoadingIndicator variant="primary" />;
  }

  return (
    <Flex flexDirection="column" gap={16}>
      {aiExtractionEnabled &&
        extractionStatus === AgentJobStatus.Failed &&
        !isExtractionFailedBannerDismissed &&
        !values.documentType && (
          <Banner
            title="Unable to extract data from document"
            variant="warning"
            onClear={() => setIsExtractionFailedBannerDismissed(true)}
            description={
              <>
                Please{" "}
                <Button
                  className={createManualProfileButton}
                  variant="neutral"
                  displayAsLink="underline"
                  onClick={handleCreateProfileClick}
                >
                  create the profile manually
                </Button>{" "}
                or re-upload a new document.
              </>
            }
          />
        )}
      <FormSelect
        full
        label={
          <AIFieldLabel
            label="Related profile"
            showIcon={Boolean(aiExtractionEnabled && matchingProfile && profileSelectedByAi)}
          />
        }
        placeholder="Select a profile"
        name="relatedProfile"
        disabled={readonly}
        dividerPosition="all"
        handleChange={isFatcaCrsEnabled ? handleProfileSelectChange : undefined}
      >
        {[
          <FormSelect.Section key="profiles">
            {allProfiles
              .filter((profile): profile is NonNullable<typeof profile> => profile?.id != null)
              .map((profile) => (
                <FormSelect.Option key={profile.id} value={String(profile.id)}>
                  {getProfileName(profile)}
                </FormSelect.Option>
              ))}
          </FormSelect.Section>,
          ...(aiExtractionEnabled && !readonly && !hideChildProfileCreation
            ? [
                <FormSelect.Section key="create-new">
                  <FormSelect.Option value="create-new">
                    <Button
                      className={createNewProfile}
                      icon="plus"
                      displayAsLink={true}
                      isFullWidth
                      onClick={handleCreateProfileClick}
                    >
                      Create new profile
                    </Button>
                  </FormSelect.Option>
                </FormSelect.Section>,
              ]
            : []),
        ]}
      </FormSelect>
      {aiExtractionEnabled && cardConfig && (
        <DocumentExtractionInfoCard
          config={cardConfig}
          differenceCount={differenceCount}
          readonly={readonly}
        />
      )}
      {currentlySelectedProfile !== undefined && (
        <>
          <Flex gap={8}>
            <LabeledData
              variant="standard"
              label="Legal name"
              value={getProfileName(currentlySelectedProfile)}
            />
            {aiExtractionEnabled && !readonly && (
              <Button
                displayAsLink={true}
                onClick={handleEditProfile}
                className={editProfileButton}
              >
                Edit profile{" "}
              </Button>
            )}
          </Flex>
          {currentlySelectedProfile.complianceObjectType === "individual" ? (
            <LabeledData
              variant="standard"
              label="Date of birth"
              value={
                currentlySelectedProfile.dateOfBirth
                  ? formatShortFormDate(currentlySelectedProfile.dateOfBirth)
                  : "--"
              }
            />
          ) : (
            <LabeledData
              variant="standard"
              label="Date of formation"
              value={
                currentlySelectedProfile.dateOfOrganization
                  ? formatShortFormDate(currentlySelectedProfile.dateOfOrganization)
                  : "--"
              }
            />
          )}

          {currentlySelectedProfile.addresses.length !== 0 && (
            <AddressViewOnly
              city={currentlySelectedProfile?.addresses[0].city ?? ""}
              street={currentlySelectedProfile?.addresses[0].street ?? ""}
              street1={currentlySelectedProfile?.addresses[0]?.street2 ?? ""}
              hasAddress
              showAsTable
              stateCode={currentlySelectedProfile?.addresses[0].state?.code ?? ""}
              province={currentlySelectedProfile?.addresses[0].province ?? ""}
              zipcode={currentlySelectedProfile?.addresses[0].postalCode ?? ""}
              country={currentlySelectedProfile?.addresses[0].country?.name}
            />
          )}
        </>
      )}
      <ResetDocumentDataWarningModal
        show={showResetModal}
        onHide={handleResetCancel}
        onConfirm={handleResetConfirm}
        changeDetails={pendingChange}
        isLoading={isModalLoading}
      />
    </Flex>
  );
};
